// Builds the field set for the customrecord_customer_requirements record created
// on Save and Submit — a faithful port of the CREATE_CUSTOMER_REQUIREMENTS
// client script. Selects resolve to their list-value internal id (the answer's
// "Variable" custom var); inputs pass through their value. UNIQUE1/UNIQUE2 come
// from the launch context (custbody101 / custbody102 off the sales order).

import type { AnswerState, Question } from "../types";
import type { LaunchContext } from "./launchContext";

// The custom form for the record (matches the script's formid).
const FORM_ID = 728;

// "No selection" — mirrors the script's isEmptySelection (handles CPQ falsey
// tokens like "F"/"false"/"null"/"none" and blanks).
function isEmptySelection(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === "boolean") return v === false;
  const s = String(v).trim();
  if (s === "") return true;
  const l = s.toLowerCase();
  return l === "f" || l === "false" || l === "null" || l === "none";
}

export type CustomerRequirementsFields = Record<string, string | number | null>;

export function buildCustomerRequirements(
  answers: AnswerState,
  questions: Question[],
  ctx: LaunchContext
): CustomerRequirementsFields {
  const byCode = new Map(questions.map((q) => [q.custrecord_cpqc_question_code, q]));

  const selectedCode = (qCode: string): string | null => {
    const q = byCode.get(qCode);
    if (!q) return null;
    for (const a of q._answers) {
      if (answers[`${qCode}/${a.custrecord_cpqc_answer_code}`] === true) {
        return a.custrecord_cpqc_answer_code;
      }
    }
    return null;
  };

  // Internal id for a select = the chosen answer's "Variable" custom var.
  const selectId = (qCode: string): string | null => {
    const code = selectedCode(qCode);
    if (isEmptySelection(code)) return null;
    const q = byCode.get(qCode);
    const ans = q?._answers.find((a) => a.custrecord_cpqc_answer_code === code);
    let v: unknown;
    try {
      const vars = JSON.parse((ans?.custrecord_cpqc_answer_custom_vars as string) || "{}");
      v = vars.Variable;
    } catch {
      v = null;
    }
    return isEmptySelection(v) ? null : String(v);
  };

  const inputVal = (qCode: string, aCode: string): string | null => {
    const v = answers[`${qCode}/${aCode}`];
    return isEmptySelection(v) ? null : String(v);
  };

  // Numeric input -> a JS number (null when blank). REST is strict about field
  // types: custrecord_requiredheatduty is an INTEGER field, so a string like
  // "1840000" is rejected with HTTP 400 ("Unable to parse value … from the
  // Integer field") and the whole create fails. CPQ1's N/record coerced it;
  // SuiteTalk REST does not, so we coerce here. Commas/spaces are stripped and
  // the value is rounded (the field takes whole numbers).
  const numVal = (qCode: string, aCode: string): number | null => {
    const v = answers[`${qCode}/${aCode}`];
    if (isEmptySelection(v)) return null;
    const n = Number(String(v).replace(/[,\s]/g, ""));
    return Number.isFinite(n) ? Math.round(n) : null;
  };

  // The generated config key, reused as a Name fallback. Name is REQUIRED on the
  // record, so if the launch's custbody101 (UNIQUE1) is empty the create would
  // fail with "Please enter value(s) for: Name" — fall back to the item key
  // (always present at save) so the record is never blocked on a missing name.
  const itemKey = inputVal("HIDDEN_ID_KEY", "ID_KEY");

  return {
    custrecord_transaction_id: ctx.custbody102, // UNIQUE2
    name: ctx.custbody101 || itemKey, // UNIQUE1 (fall back to the item key — Name is required)
    customform: FORM_ID,
    custrecord_itemname_buffer: itemKey,
    custrecord_customformbuffer: FORM_ID,
    // dropdown/selects -> list-value internal id (null when not selected)
    custrecord_productionunit: selectId("APPLICATION"),
    custrecord_firetubequantity: selectId("FIRETUBE_QUANTITY"),
    custrecord_vesselposition: selectId("VESSEL_POSITION"),
    custrecord_outlettoinletposition: selectId("OUTLET_INLET_POSITION"),
    custrecord_gas_type: selectId("GAS_TYPE"),
    custrecord_fieldconditions: selectId("CONDITIONS"),
    custrecord_burnerqty: selectId("BURNER_QTY"),
    custrecord_ndbordertype: selectId("ORDER_TYPE"),
    custrecord_producttype: selectId("PRODUCT_TYPE"),
    // numeric/text inputs -> null when blank/falsey
    // requiredheatduty is an INTEGER field -> send a number, never a string.
    custrecord_requiredheatduty: numVal("REQUIRED_HEAT_DUTY", "REQUIRED_HEAT_DUTY"),
    custrecord_firetubesize: inputVal("FIRETUBE_DIAMETER", "FIRETUBE_DIAMETER"),
    custrecord_firetubetotalrunlength: inputVal("FIRETUBE_LENGTH", "FIRETUBE_LENGTH"),
    custrecord_basecellsize: inputVal("BASECELLSIZE", "BASECELLSIZE"),
    custrecord_fuelpressure: inputVal("FUEL_PRESSURE", "FUEL_PRESSURE"),
    custrecord_competitorname: inputVal("COMPETITOR_NAME", "COMPETITOR_NAME"),
    custrecord_productname: inputVal("PRODUCT_NAME", "PRODUCT_NAME"),
  };
}

// Reference (List/Record) fields — the proxy types these as { id } on create.
export const CUSTOMER_REQUIREMENTS_REFERENCE_FIELDS = [
  "custrecord_productionunit",
  "custrecord_firetubequantity",
  "custrecord_vesselposition",
  "custrecord_outlettoinletposition",
  "custrecord_gas_type",
  "custrecord_fieldconditions",
  "custrecord_burnerqty",
  "custrecord_ndbordertype",
  "custrecord_producttype",
];
